import React, { useState,useEffect } from "react";
import { apiClient } from "../../../apiclient/apiclient";
import {
  Clock,
  Truck,
  PackageCheck,
  CheckCircle,
  Package
} from "lucide-react";
import { toast } from "react-toastify";

  const OrderCard = ({ order, reload, viewOnly = false, onClose }) => {
 const [paymentInput, setPaymentInput] = useState({
    mode: "Cash",
    amount: ""
  });
  const [loadingAction, setLoadingAction] = useState(null);

  // ✅ LOCAL payments for instant UI update
  const [paymentsState, setPaymentsState] = useState(
    order.paymentDetails?.payments || []
  );

  // ✅ LOCAL discount
  const [discountState, setDiscountState] = useState(
    Number(order.paymentDetails?.discount || 0)
  );


const Spinner = () => (
  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-orange-100 text-orange-700";
      case "processing":
      case "shipped":
        return "bg-blue-100 text-blue-700";
      case "delivered":
        return "bg-purple-100 text-purple-700";
      case "completed":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="w-5 h-5 text-orange-600" />;
      case "processing":
      case "shipped":
        return <Truck className="w-5 h-5 text-blue-600" />;
      case "delivered":
        return <PackageCheck className="w-5 h-5 text-purple-600" />;
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      default:
        return <Package className="w-5 h-5 text-gray-600" />;
    }
  };
useEffect(() => {
  setPaymentsState(order.paymentDetails?.payments || []);
  setDiscountState(Number(order.paymentDetails?.discount || 0));
}, [order._id, order.paymentDetails]);

  // ---------------------------
  // PAYMENT CALCULATION (SAFE)
  // ---------------------------
const totalAmount =
    order.paymentDetails?.totalAmount ??
    order.items.reduce(
      (sum, i) => sum + Number(i.price || 0) * Number(i.quantity || 0),
      0
    );

  const calculatedTotalPaid = paymentsState.reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0
  );

  const totalPaid = Math.max(calculatedTotalPaid, order.paymentDetails?.paidAmount || 0);

  const payable = Math.max(totalAmount - discountState, 0);
  const balanceAmount = Math.max(payable - totalPaid, 0);

  const paymentStatus =
    totalPaid === 0
      ? "unpaid"
      : totalPaid >= payable
      ? "paid"
      : "partial";

  /* ================= ADD PAYMENT ================= */
  const handleAddPayment = async () => {
    
    const amount = Number(paymentInput.amount);

    if (!amount || amount <= 0) {
      toast.error("Enter valid amount");
      return;
    }

    if (amount > balanceAmount) {
      toast.error(`Cannot collect more than ₹${balanceAmount}`);
      return;
    }

    // ✅ INSTANT UI UPDATE
    const newPayment = {
      mode: paymentInput.mode,
      amount
    };

    setPaymentsState(prev => [...prev, newPayment]);

    try {
       setLoadingAction("payment");
      await apiClient.put(`/orders/${order._id}/payment`, {
        payment: newPayment,
        discount: discountState
      });

      toast.success("Payment added");
      setPaymentInput({ mode: "Cash", amount: "" });
      reload(); // backend sync

    } catch {
      toast.error("Failed to update payment");
      // rollback
      setPaymentsState(prev => prev.slice(0, -1));
    }
    finally {setLoadingAction(null);}
  };
  // ---------------------------
  // COLLECT ITEM
  // ---------------------------
 const handleCollect = async (orderId, itemId) => {
  try {
    setLoadingAction(`collect-${itemId}`);
    await apiClient.put(`/orders/${orderId}/collect-item/${itemId}`);
    toast.success("Item collected");
    reload();
  } catch {
    toast.error("Failed to collect item");
  } finally {
    setLoadingAction(null);
  }
};

const handleAccept = async () => {
  try {
    setLoadingAction("accept");
    await apiClient.put(`/orders/${order._id}/accept`);
    toast.success("Order accepted");
    reload();
  } catch {
    toast.error("Failed to accept order");
  } finally {
    setLoadingAction(null);
  }
};

  // ---------------------------
  // UPDATE STATUS
  // ---------------------------
  const updateStatus = async (orderId, newStatus) => {
    try {
      setLoadingAction(`status-${newStatus}`);
      await apiClient.put(`/orders/${orderId}/status`, { status: newStatus });
      toast.success("Status updated");
      reload();
    } catch {
      toast.error("Failed to update status");
    } finally {
      setLoadingAction(null);
    }
  };
const handleMarkCompleted = async () => {
  // If balance exists, ask confirmation
  if (balanceAmount > 0) {
    const confirm = window.confirm(
      `₹${balanceAmount} is still pending.\nDo you want to mark this order as completed anyway?`
    );

    if (!confirm) return; // ❌ user cancelled
  }

  // ✅ proceed to complete
  await updateStatus(order._id, "completed");
};


return (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4 mb-4">

    {/* ================= HEADER ================= */}
    <div className="flex justify-between items-start pb-3 border-b border-gray-100">

      <div className="space-y-1">
        <h3 className="text-base font-semibold text-blue-900">
          {order.orderId}
        </h3>

        <p className="text-sm font-medium text-gray-700">
          {order.clientId?.name}
        </p>

        <p className="text-xs text-gray-500">
          {order.clientId?.phone}
        </p>

        <p className="text-xs text-gray-500">
          {order.clientId?.address}
        </p>

        <p className="text-[11px] text-gray-400">
          {new Date(order.createdAt).toLocaleDateString("en-IN")}
        </p>
      </div>

      <span
        className={`px-4 py-1.5 rounded-full text-xs font-semibold shadow-sm ${getStatusColor(order.status)}`}
      >
        {order.status.toUpperCase()}
      </span>
    </div>

    {/* ================= ITEMS ================= */}
    <div className="space-y-3 pb-5 border-b border-gray-100">
      {order.items?.map((item, idx) => (
        <div key={idx} className="space-y-1">

          <p className="text-sm font-medium text-gray-800">
            {item.productName} ({item.quantityValue}{item.quantityUnit})
          </p>

          <div className="flex justify-between items-center">

            <div className="text-xs text-gray-500 leading-tight">
              <p>Qty: {item.quantity} | ₹{item.price} × {item.quantity}</p>
              <p>Warehouse: {item.warehouseName || "N/A"}</p>
            </div>

            {order.status === "processing" && (
<button
  disabled={item.collected || loadingAction === `collect-${item._id}`}
  onClick={() => handleCollect(order._id, item._id)}
  className={`
    px-4 py-2
    text-sm font-semibold
    rounded-xl
    min-w-[90px]
    transition-all duration-200
    flex items-center justify-center
    ${
      item.collected
        ? "bg-green-100 text-green-700 cursor-not-allowed"
        : "bg-blue-900 text-white hover:bg-amber-500 active:scale-95"
    }
  `}
>
  {loadingAction === `collect-${item._id}` ? (
    <Spinner />
  ) : item.collected ? (
    "Collected"
  ) : (
    "Collect"
  )}
</button>



            )}

          </div>
        </div>
      ))}
    </div>

    {/* ================= PAYMENT SUMMARY ================= */}
    <div className="space-y-3 text-sm">
              
      <p className="font-semibold text-gray-800">Payment Summary</p>

      {/* Payment List */}
      {/* Payment List */}
      {paymentsState.length === 0 && (!order.paymentDetails?.paidAmount || order.paymentDetails.paidAmount <= calculatedTotalPaid) ? (
        <p className="text-gray-400 text-xs">No payments yet</p>
      ) : (
        <div className="space-y-1">
          {paymentsState
            .filter(p => Number(p.amount) > 0)
            .map((p, idx) => (
              <div key={idx} className="flex justify-between text-gray-700">
                <span>{p.mode}</span>
                <span className="font-semibold">₹{p.amount}</span>
              </div>
            ))}
          {(() => {
            const extraPaid = (order.paymentDetails?.paidAmount || 0) - calculatedTotalPaid;
           
            if (extraPaid > 0) {
              return (
                <div className="flex justify-between text-green-700">
                  <span>Office Updated</span>
                  <span className="font-semibold">₹{extraPaid}</span>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Totals */}
      <div className="space-y-0.5">
        <div className="flex justify-between">
          <span>Total</span>
          <span className="font-semibold">₹{totalAmount}</span>
        </div>

        <div className="flex justify-between">
          <span>Discount</span>
          <span className="font-semibold">- ₹{discountState}</span>
        </div>

        <div className="flex justify-between">
          <span>Payable</span>
          <span className="font-semibold">₹{payable}</span>
        </div>

        <div className="flex justify-between ">
          <span>Paid</span>
          <span className="font-semibold">₹{totalPaid - (order.paymentDetails?.extraPaid || 0)}</span>
        </div>

        <div className="flex justify-between">
          <span>Balance</span>
          <span className="font-semibold">₹{balanceAmount}</span>
        </div>
      </div>

      {/* Payment Status */}
     <div className="mt-1">
          <strong>Status:</strong>{" "}
          <span className={`font-semibold ${
            paymentStatus === "paid"
              ? "text-green-600"
              : paymentStatus === "partial"
              ? "text-orange-600"
              : "text-red-600"
          }`}>
            {paymentStatus}
          </span>
        </div>

      {/* ADD PAYMENT SECTION */}
      {order.status === "delivered" && paymentStatus !== "paid" && (
        <div className="space-y-2 pt-2">

          {/* Mode + Amount */}
          <div className="flex gap-2">
            <select
              value={paymentInput.mode}
              onChange={(e) =>
                setPaymentInput({ ...paymentInput, mode: e.target.value })
              }
              className="border rounded-lg px-2 py-1.5 text-xs w-24
              focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option>Cash</option>
              <option>UPI</option>
              <option>Card</option>
              <option>Bank</option>
            </select>

            <input
              type="number"
              value={paymentInput.amount}
              onChange={(e) =>
                setPaymentInput({ ...paymentInput, amount: e.target.value })
              }
              placeholder="Amount"
              className="border rounded-lg px-2 py-1.5 text-xs flex-1
              focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Discount + Add */}
          <div className="flex gap-2 items-end">

            <div className="w-24">
              <label className="text-[10px] text-gray-400">Discount</label>
              <input
                type="number"
                value={discountState}
                onChange={(e) =>
                  setDiscountState(Number(e.target.value || 0))
                }
                className="border rounded-lg px-2 py-1.5 text-xs w-full mt-1
                focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <button
              disabled={loadingAction === "payment"}
              onClick={handleAddPayment}
              className="flex-1 py-2 rounded-lg text-sm font-semibold text-white
              bg-blue-900 hover:bg-amber-500 transition-all duration-200
              flex items-center justify-center"
            >
              {loadingAction === "payment" ? <Spinner /> : "Add Payment"}
            </button>

          </div>
        </div>
      )}
    </div>

    {/* ================= ACTION BUTTONS ================= */}
    <div className="pt-3 space-y-2">

      {order.status === "pending" && !order.acceptedByDeliveryBoy && (
        <button
          disabled={loadingAction === "accept"}
          onClick={handleAccept}
          className="w-full py-2 rounded-lg text-sm font-semibold
          bg-blue-900 hover:bg-amber-500 transition-all duration-200
          text-white flex items-center justify-center gap-2"
        >
          {loadingAction === "accept" ? <Spinner /> : "Accept Order"}
        </button>
      )}

      {order.items?.every(i => i.collected) &&
        order.status === "processing" && (
        <button
          disabled={loadingAction === "shipped"}
          onClick={() => updateStatus(order._id, "shipped")}
          className="w-full py-2 rounded-lg text-sm font-semibold
          bg-blue-900 hover:bg-amber-500 transition-all duration-200
          text-white flex items-center justify-center gap-2"
        >
          {loadingAction === "shipped" ? <Spinner /> : "Mark Shipped"}
        </button>
      )}

      {order.status === "shipped" && (
        <button
          disabled={loadingAction === "delivered"}
          onClick={() => updateStatus(order._id, "delivered")}
          className="w-full py-2 rounded-lg text-sm font-semibold
          bg-blue-900 hover:bg-amber-500 transition-all duration-200
          text-white flex items-center justify-center gap-2"
        >
          {loadingAction === "delivered" ? <Spinner /> : "Mark Delivered"}
        </button>
      )}

      {order.status === "delivered" && (
        <button
          disabled={loadingAction === "completed"}
          onClick={() => updateStatus(order._id, "completed")}
          className="w-full py-2 rounded-lg text-sm font-semibold
          bg-blue-900 hover:bg-amber-500 transition-all duration-200
          text-white flex items-center justify-center gap-2"
        >
          {loadingAction === "completed" ? <Spinner /> : "Mark Completed"}
        </button>
      )}

    </div>

  </div>
);

};

export default OrderCard;
